import {useQueryClient} from '@tanstack/react-query'
import moment from 'moment'
import {Timeline} from 'primereact/timeline'
import {useCallback, useMemo, useRef, useState} from 'react'
import InfiniteScroll from 'react-infinite-scroller'
import {ReactComponent as FaUserInjured} from '../../assets/icons/FaUserInjured.svg'
import {ReactComponent as SymptomHistory} from '../../assets/images/history_symptom.svg'
import TranslateUtils from '../../i18n/TranslateUtils'
import {QueryKeyTypes} from '../../query-manager/QueryKeyTypes'
import {QueryKeyManager} from '../../query-manager/QueryKeysManager'
import {useAuth} from '../authentication/AuthProvider'
import {isArrayAndNotEmpty} from '../observations/Utils'
import {UserPropertyType} from '../user-property/UserPropertyConstants'
import {getPageUserSymptoms} from '../user-property/UserPropertyEndPoint'
import {usePageUserSymptoms} from '../user-property/query-keys/UserPropertyQueryManager'

const InitialFilterRequest = {}
const InitialPageable = {
    pageRows: 10,
    pageNumber: 0,
    sortField: 'id',
    sortOrder: -1,
}

//TODO: Handle Fetch more Patient Symptoms
const PatientMedicalHistory = ({userId}) => {
    //#region Context
    const {user} = useAuth()
    //#endregion

    //#region Translate
    const {translateCommons} = TranslateUtils()
    //#endregion

    //#region State
    const [pageable, setPageable] = useState({...InitialPageable})
    const [filterData, setFilterData] = useState({
        ...InitialFilterRequest,
    })
    //#endregion

    const pageNumberRef = useRef(0)

    //#region Query
    const queryClient = useQueryClient()

    const {userPropertyKeys} = QueryKeyManager(
        QueryKeyTypes.userProperty,
        user?.id,
    )

    const userSymptomQueryKeys = useMemo(() => {
        return userPropertyKeys.page({
            type: UserPropertyType.userSymptom,
            pageNumber: pageable.pageNumber,
            pageRows: pageable.pageRows,
            sortField: pageable.sortField,
            sortOrder: pageable.sortOrder,
            filterRequest: {...filterData, userId: userId},
        })
    }, [
        userPropertyKeys,
        pageable.pageNumber,
        pageable.pageRows,
        pageable.sortField,
        pageable.sortOrder,
        filterData,
        userId,
    ])

    const {data: symptomData} = usePageUserSymptoms(
        user?.id,
        0,
        pageable.pageRows,
        pageable.sortField,
        pageable.sortOrder,
        {...filterData, userId: userId},
        true,
    )

    //#endregion

    const handleFetchMore = useCallback(async () => {
        if (pageNumberRef.current >= symptomData?.totalPages) {
            return
        }

        const nextSymptomData = await getPageUserSymptoms(
            user?.id,
            pageNumberRef.current + 1,
            pageable.pageRows,
            pageable.sortField,
            pageable.sortOrder,
            {...filterData, userId: userId},
        )

        if (isArrayAndNotEmpty(nextSymptomData?.content)) {
            const {userPropertyKeys} = QueryKeyManager(
                QueryKeyTypes.userProperty,
                user?.id,
            )
            queryClient.setQueryData(
                userPropertyKeys.page({
                    type: UserPropertyType.userSymptom,
                    pageNumber: 0,
                    pageRows: pageable.pageRows,
                    sortField: pageable.sortField,
                    sortOrder: pageable.sortOrder,
                    filterRequest: {...filterData, userId: userId},
                }),
                oldData => {
                    return {
                        ...oldData,
                        first: nextSymptomData?.first,
                        last: nextSymptomData?.last,
                        content: [
                            ...oldData.content,
                            ...nextSymptomData.content,
                        ],
                    }
                },
            )

            pageNumberRef.current++
        }
    }, [symptomData?.totalPages, user?.id, userId])

    // const {mutate: handleReload} = useMutation(data => data, {
    //     onSuccess: data => {
    //         if (!!data?.id) {
    //             queryClient.setQueryData(
    //                 [QueryKey, user?.id, userId],
    //                 oldData => {
    //                     if (
    //                         !oldData ||
    //                         !oldData?.content ||
    //                         oldData?.length === 0
    //                     ) {
    //                         return {
    //                             content: [data],
    //                         }
    //                     } else {
    //                         let tmp = [...oldData.content]
    //                         let existingId = tmp.findIndex(
    //                             x => x.id === data.id,
    //                         )

    //                         if (existingId >= 0) {
    //                             tmp[existingId] = data
    //                         } else {
    //                             tmp = [data, ...tmp]
    //                         }

    //                         return {
    //                             ...oldData,
    //                             content: tmp,
    //                         }
    //                     }
    //                 },
    //             )
    //         }
    //     },
    // })

    // const handleReloadData = useCallback(
    //     data => {
    //         if (!!data?.id) {
    //             handleReload(data)
    //         }
    //     },
    //     [handleReload],
    // )

    // useEffect(() => {
    //     if (!!userId) {
    //         detailEvent.on(
    //             'DETAIL-INFORMATION-RELOAD-' + QueryKey + userId,
    //             handleReloadData,
    //         )

    //         return () => {
    //             removeDetailInformationReload(
    //                 'DETAIL-INFORMATION-RELOAD-' + QueryKey + userId,
    //             )
    //         }
    //     }
    // }, [userId, handleReloadData])

    const groupItems = useCallback(
        (array = symptomData?.content, property = 'createdAt') => {
            if (array && Array.isArray(array) && array.length > 0) {
                return array.reduce(function (groups, item) {
                    let name = moment(item[property]).format('MMMM Do YYYY')
                    let group = groups[name] || (groups[name] = [])
                    group.push(item)
                    return groups
                }, {})
            } else {
                return {}
            }
        },
        [symptomData],
    )
    const symptomsGroupData = useMemo(() => {
        let data = groupItems()
        return Object.keys(data).map(date => {
            return {
                date,
                data: data[date],
            }
        })
    }, [groupItems])

    const customizedMarker = item => {
        let color = 'darkgray'
        let id = symptomData?.content[0].id
        if (item.id === id) {
            color = 'rgb(23,187,173)'
        }
        return (
            <span
                className="flex w-1rem h-1rem align-items-center justify-content-center text-white border-circle z-1 shadow-1"
                style={{border: `2px solid ${color}`}}></span>
        )
    }
    const content = item => {
        // let color = 'rgb(23,187,173)'
        // let id = symptomData?.content[0].id
        // if (item.id === id) {
        //     color = '#6366F1'
        // }
        return (
            <div className=" mb-2 bg-white border-1 border-white p-2 border-round-md hover:border-gray-100">
                <h6 className="font-bold m-0 p-0 mb-1">
                    {moment(item.createdAt).format('H:mm')}{' '}
                </h6>
                <div className="">{translateCommons(item.symptom)} </div>
            </div>
        )
    }
    return (
        <div
            className="col-12 border-round-2xl p-0"
            style={{
                background: '#f9f9f9',
            }}>
            <div className="py-3">
                <h3 className="font-bold text-center">
                    <span className="mr-3 pt-1">
                        <FaUserInjured
                            style={{width: '24px', height: '24px'}}
                            className="pt-1"
                        />
                    </span>
                    {translateCommons('Symptom History')}
                </h3>
            </div>
            <div
                className="col-12 p-0"
                style={{
                    overflow: 'scroll',
                    height: '80vh',
                }}>
                {!symptomsGroupData || symptomsGroupData?.length < 1 ? (
                    <div className="w-full h-full flex flex-column justify-content-center align-items-center">
                        <SymptomHistory height={'15rem'} />

                        <div className="my-4">
                            <h6>
                                {translateCommons(
                                    'There is currently no medical history of symptoms for this patient.',
                                )}
                            </h6>
                        </div>
                    </div>
                ) : (
                    <InfiniteScroll
                        style={{
                            height: 'max-content',
                        }}
                        pageStart={pageable?.pageNumber || 0}
                        loadMore={handleFetchMore}
                        hasMore={!symptomData?.last}
                        loader={
                            symptomData &&
                            !!symptomData?.last && (
                                <div className="loader" key={0}>
                                    {translateCommons('Loading')}...
                                </div>
                            )
                        }
                        useWindow={false}>
                        {symptomsGroupData.map(item => (
                            <div className="col-12 " key={item.date}>
                                <h5
                                    className="font-bold "
                                    style={{
                                        color: '#0D9488',
                                    }}>
                                    {item.date}
                                </h5>
                                <Timeline
                                    className="ml-5"
                                    value={item.data}
                                    align="left"
                                    content={content}
                                    marker={customizedMarker}
                                />
                            </div>
                        ))}
                    </InfiniteScroll>
                )}
            </div>
        </div>
    )
}

export default PatientMedicalHistory
